import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr, fmtIST } from "../lib/api";
import { useAuth } from "../lib/auth";
import Shell, { PageHeader } from "../components/Shell";
import { hasPerm } from "../components/Sidebar";
import { FilePdf, WhatsappLogo, PencilSimple, Prohibit, ClockCounterClockwise, X, Plus, Minus, Copy, ArrowSquareOut, Download, CheckCircle, ArrowUUpLeft, Lightning } from "@phosphor-icons/react";
import ComplianceModal from "../components/ComplianceModal";
import ApprovalModal from "../components/ApprovalModal";
import { computePosTotals, apiErrorMessage, apiErrorCode } from "../lib/pricing";
import { Lock, ShieldCheck } from "@phosphor-icons/react";

function within1Hour(iso) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Date.now() - t <= 60 * 60 * 1000;
}
function minutesLeft(iso) {
  const elapsed = (Date.now() - new Date(iso).getTime()) / 60000;
  return Math.max(0, Math.ceil(60 - elapsed));
}

export default function Invoices() {
  const { storeId } = useParams();
  const { user } = useAuth();
  const canEdit = hasPerm(user, "invoices.edit");
  const canCancel = hasPerm(user, "invoices.cancel");
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [history, setHistory] = useState(null);
  const [creditNoteSale, setCreditNoteSale] = useState(null);
  const canRefund = hasPerm(user, "pos.refund");

  const load = async () => {
    const r = await api.get(`/store/${storeId}/sales`);
    setList(r.data);
  };
  useEffect(() => { load(); }, [storeId]);

  const openPdf = async (id) => {
    const token = localStorage.getItem("pos_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/sales/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const [shareDialog, setShareDialog] = useState(null);
  const [complianceSale, setComplianceSale] = useState(null);
  const now = new Date();
  const [expFrom, setExpFrom] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString("en-CA"));
  const [expTo, setExpTo] = useState(now.toLocaleDateString("en-CA"));
  const [exporting, setExporting] = useState(false);
  const shareWhatsApp = async (s) => {
    try {
      const r = await api.get(`/store/sales/${s.id}/whatsapp`);
      setShareDialog({
        invoice: s,
        wa_url: r.data.url,
        download_url: r.data.download_url,
        text: r.data.text,
        phone: r.data.phone,
        expires_at: r.data.expires_at,
      });
    } catch (e) {
      alert("Could not generate share link: " + (e.response?.data?.detail || e.message));
    }
  };

  const cancelInvoice = async (s) => {
    if (!window.confirm(`Cancel invoice ${s.invoice_no}?\nStock will be restored.`)) return;
    try {
      await api.post(`/store/sales/${s.id}/cancel`);
      load();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const openHistory = async (s) => {
    const r = await api.get(`/audit/entity/sale/${s.id}`);
    setHistory({ sale: s, entries: r.data });
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("pos_token");
      const params = new URLSearchParams();
      if (expFrom) params.set("from_date", expFrom);
      if (expTo) params.set("to_date", expTo);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/${storeId}/sales/export.xlsx?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Store sale B2C & B2B ${expFrom || ""} to ${expTo || ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert("Export failed: " + e.message);
    } finally { setExporting(false); }
  };

  return (
    <Shell>
      <PageHeader title="Invoices" subtitle={user?.role === "super_admin" ? "As admin you can edit or cancel any invoice. All changes are audited." : "Edit or cancel within 1 hour of creation. After that, invoices are immutable."} testId="invoices-header" />
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="export-bar">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">From</label>
            <input type="date" value={expFrom} onChange={(e) => setExpFrom(e.target.value)} data-testid="export-from" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">To</label>
            <input type="date" value={expTo} onChange={(e) => setExpTo(e.target.value)} data-testid="export-to" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
          </div>
          <button onClick={exportXlsx} disabled={exporting} data-testid="export-xlsx-btn" className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-md disabled:opacity-50 inline-flex items-center gap-1.5">
            <Download size={15} weight="bold" /> {exporting ? "Preparing…" : "Export Excel (B2C + B2B)"}
          </button>
          <div className="text-xs text-slate-400 pb-2.5">Tally-style sheet, one row per item · defaults to this month</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Invoice #</th>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Customer</th>
                <th className="text-left px-4 py-3 font-semibold">Phone</th>
                <th className="text-left px-4 py-3 font-semibold">Payment</th>
                <th className="text-right px-4 py-3 font-semibold">Items</th>
                <th className="text-right px-4 py-3 font-semibold">Total</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-slate-400">No invoices yet.</td></tr>}
              {list.map((s) => {
                const editable = !s.cancelled && (user?.role === "super_admin" || within1Hour(s.created_at));
                return (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono-num font-semibold">{s.invoice_no}</td>
                    <td className="px-4 py-3 font-mono-num text-slate-600">{fmtIST(s.created_at)}</td>
                    <td className="px-4 py-3">
                      <div>{s.customer_name}</div>
                      {s.executive_name && <div className="text-[10px] text-slate-400">Exec: {s.executive_name}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono-num text-slate-600">{s.customer_phone || "—"}</td>
                    <td className="px-4 py-3 uppercase text-[10px] tracking-widest font-semibold text-slate-700" data-testid={`inv-payment-${s.invoice_no}`}>
                      {s.payments && s.payments.length > 1 ? (
                        <div className="space-y-0.5">
                          <div className="text-emerald-700 font-bold">Split · {s.payments.length}</div>
                          {s.payments.map((p, idx) => (
                            <div key={idx} className="font-mono-num normal-case text-[10px] text-slate-600">
                              {p.method.toUpperCase()} {inr(p.amount)}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span>{s.payment_method}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-num">{s.items.length}</td>
                    <td className="px-4 py-3 text-right font-mono-num font-bold">{inr(s.grand_total)}</td>
                    <td className="px-4 py-3">
                      {s.cancelled ? (
                        <span className="inline-block px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-full text-[10px] uppercase tracking-widest font-bold">Cancelled</span>
                      ) : s.edited_at ? (
                        <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-[10px] uppercase tracking-widest font-bold">Edited</span>
                      ) : editable ? (
                        <span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[10px] uppercase tracking-widest font-bold">Editable · {minutesLeft(s.created_at)}m</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 bg-slate-100 text-slate-700 border border-slate-200 rounded-full text-[10px] uppercase tracking-widest font-bold">Final</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openPdf(s.id)} data-testid={`inv-pdf-${s.invoice_no}`} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900 mr-3"><FilePdf size={14} />PDF</button>
                      <button onClick={() => shareWhatsApp(s)} data-testid={`inv-wa-${s.invoice_no}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 mr-3"><WhatsappLogo size={14} weight="fill" />WA</button>
                      {editable && (
                        <>
                          {canEdit && <button onClick={() => setEditing({ ...s })} data-testid={`inv-edit-${s.invoice_no}`} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-800 mr-3"><PencilSimple size={14} />Edit</button>}
                          {canCancel && <button onClick={() => cancelInvoice(s)} data-testid={`inv-cancel-${s.invoice_no}`} className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 hover:text-red-800 mr-3"><Prohibit size={14} />Cancel</button>}
                        </>
                      )}
                      {canRefund && !s.cancelled && (
                        <button onClick={() => setCreditNoteSale(s)} data-testid={`inv-cn-${s.invoice_no}`} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-800 mr-3"><ArrowUUpLeft size={14} />CN</button>
                      )}
                      {!s.cancelled && (() => {
                        const ei = s.einvoice || {};
                        const hasIrn = ei.irn && ei.status !== "cancelled";
                        const hasEwbOnly = !hasIrn && (s.ewaybill || {}).ewb_no;
                        const eligible = hasIrn || hasEwbOnly || (s.customer_gstin || "").trim() || (s.grand_total || 0) > 50000;
                        if (!eligible) return null;
                        return (
                          <button onClick={() => setComplianceSale(s)} data-testid={`inv-gst-${s.invoice_no}`}
                            className={`inline-flex items-center gap-1 text-xs font-semibold mr-3 ${hasIrn || hasEwbOnly ? "text-emerald-700 hover:text-emerald-800" : "text-purple-700 hover:text-purple-800"}`}>
                            <Lightning size={14} weight={hasIrn || hasEwbOnly ? "fill" : "regular"} />
                            {hasIrn ? "IRN ✓" : hasEwbOnly ? "EWB ✓" : "e-Inv"}
                          </button>
                        );
                      })()}
                      <button onClick={() => openHistory(s)} data-testid={`inv-hist-${s.invoice_no}`} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"><ClockCounterClockwise size={14} />History</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-sm">
          <Link to={`/store/${storeId}/credit-notes`} data-testid="link-credit-notes" className="font-semibold text-amber-700 hover:underline">View issued Credit Notes →</Link>
        </div>
      </div>

      {editing && <EditModal sale={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creditNoteSale && <CreditNoteModal sale={creditNoteSale} onClose={() => setCreditNoteSale(null)} onSaved={() => { setCreditNoteSale(null); load(); }} /> }
      {history && <HistoryModal data={history} onClose={() => setHistory(null)} />}
      {shareDialog && <ShareDialog data={shareDialog} onClose={() => setShareDialog(null)} />}
      {complianceSale && <ComplianceModal sale={complianceSale} onClose={() => setComplianceSale(null)} onSaved={() => load()} />}
    </Shell>
  );
}

const PAYMENT_METHODS = ["cash", "upi", "card", "other", "pending", "credit_note"];

function CreditNoteModal({ sale, onClose, onSaved }) {
  // Existing CNs against this invoice (to cap returnable quantities)
  const [creditNotes, setCreditNotes] = useState([]);
  useEffect(() => {
    api.get(`/store/${sale.store_id}/credit-notes`).then((r) => setCreditNotes(r.data || [])).catch(() => {});
  }, [sale.store_id]);

  // Aggregate sold qty per SKU minus what's already been credited
  const sold = useMemo(() => {
    const m = {};
    sale.items.forEach((l) => {
      if (!m[l.sku]) m[l.sku] = { ...l, origQty: 0, remaining: 0, total: 0 };
      m[l.sku].origQty += l.qty;
      m[l.sku].remaining += l.qty;
      m[l.sku].total += parseFloat(l.total) || 0;
    });
    creditNotes.filter((cn) => cn.sale_id === sale.id).forEach((cn) => {
      (cn.lines || []).forEach((l) => { if (m[l.sku]) m[l.sku].remaining -= l.qty; });
    });
    return Object.values(m).filter((l) => l.remaining > 0);
  }, [sale, creditNotes]);

  const [returns, setReturns] = useState({}); // sku -> qty
  const [reason, setReason] = useState("");
  const [restock, setRestock] = useState(true);
  const [refundMethod, setRefundMethod] = useState("cash");
  const [flatAmount, setFlatAmount] = useState("");
  const [saving, setSaving] = useState(false);

  // Bill-level discount/round-off shared proportionally (mirrors backend)
  const factor = useMemo(() => {
    const linesSum = sale.items.reduce((a, l) => a + (parseFloat(l.total) || 0), 0);
    return linesSum ? (parseFloat(sale.grand_total) || 0) / linesSum : 1;
  }, [sale]);

  const computedAmount = useMemo(() => {
    let amt = 0;
    sold.forEach((l) => {
      const rq = parseInt(returns[l.sku]) || 0;
      if (rq > 0 && l.origQty > 0) amt += (l.total / l.origQty) * rq * factor;
    });
    return Math.round(amt * 100) / 100;
  }, [returns, sold, factor]);

  const setRet = (sku, v, max) => {
    const n = Math.max(0, Math.min(max, parseInt(v) || 0));
    setReturns((r) => ({ ...r, [sku]: n }));
  };

  const submit = async () => {
    const lines = Object.entries(returns).filter(([, q]) => q > 0).map(([sku, qty]) => ({ sku, qty }));
    const flat = parseFloat(flatAmount) || 0;
    if (lines.length === 0 && flat <= 0) { alert("Select return quantities or enter a credit amount."); return; }
    setSaving(true);
    try {
      await api.post("/store/credit-notes", {
        sale_id: sale.id,
        lines,
        amount: flat > 0 ? flat : undefined,
        reason,
        restock,
        refund_method: refundMethod,
      });
      onSaved();
    } catch (e) { alert("Failed: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="credit-note-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">Credit Note</div>
            <div className="text-xs text-slate-500 font-mono-num">Against {sale.invoice_no} · {sale.customer_name || "Walk-in"} · {inr(sale.grand_total)}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Return Items (set quantity)</div>
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
              {sold.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">All items already credited — use a flat amount below.</div>}
              {sold.map((l) => (
                <div key={l.sku} className="flex items-center justify-between px-3 py-2">
                  <div className="min-w-0 pr-3">
                    <div className="text-sm font-medium truncate">{l.product_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono-num">{l.sku} · returnable {l.remaining}</div>
                  </div>
                  <input
                    type="number" min="0" max={l.remaining}
                    value={returns[l.sku] ?? ""}
                    onChange={(e) => setRet(l.sku, e.target.value, l.remaining)}
                    placeholder="0"
                    data-testid={`cn-ret-${l.sku}`}
                    className="w-20 h-9 px-2 border border-slate-300 rounded-md text-sm font-mono-num text-right"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Or Flat Credit Amount (₹)</label>
              <input type="number" step="0.01" value={flatAmount} onChange={(e) => setFlatAmount(e.target.value)} data-testid="cn-flat-amount" placeholder="Optional" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num text-right" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Refund Method</label>
              <select value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)} data-testid="cn-refund-method" className="w-full h-10 px-2 border border-slate-300 rounded-md text-sm uppercase">
                {PAYMENT_METHODS.filter((m) => m !== "pending" && m !== "credit_note").map((m) => <option key={m} value={m}>{m}</option>)}
                <option value="store_credit">store credit</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Reason</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cn-reason" placeholder="e.g. Damaged item returned" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={restock} onChange={(e) => setRestock(e.target.checked)} data-testid="cn-restock" />
            Add returned items back to stock
          </label>
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
            <div className="text-xs font-semibold text-amber-800 uppercase tracking-widest">Credit Amount</div>
            <div className="text-lg font-bold font-mono-num text-amber-900" data-testid="cn-amount-preview">
              {inr((parseFloat(flatAmount) || 0) > 0 && Object.values(returns).every((q) => !q) ? (parseFloat(flatAmount) || 0) : computedAmount)}
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="cn-submit" className="h-10 px-5 bg-amber-700 text-white rounded-md text-sm font-semibold hover:bg-amber-800 disabled:opacity-50">
            {saving ? "Creating…" : "Create Credit Note"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ sale, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [items, setItems] = useState(sale.items.map((it) => ({
    ...it,
    discount_mode: (it.discount_pct && it.discount_pct > 0) ? "percent" : "flat",
  })));
  const [customerName, setCustomerName] = useState(sale.customer_name || "");
  const [phone, setPhone] = useState(sale.customer_phone || "");
  const [address, setAddress] = useState(sale.customer_address || "");
  const [shipToDifferent, setShipToDifferent] = useState(!!sale.ship_to_different);
  const [shipToName, setShipToName] = useState(sale.ship_to_name || "");
  const [shipToPhone, setShipToPhone] = useState(sale.ship_to_phone || "");
  const [shipToAddress, setShipToAddress] = useState(sale.ship_to_address || "");
  const [notes, setNotes] = useState(sale.notes || "");
  const [discountMode, setDiscountMode] = useState((sale.discount_pct || 0) > 0 ? "percent" : "flat");
  const [discountValue, setDiscountValue] = useState((sale.discount_pct || 0) > 0 ? sale.discount_pct : (sale.discount_total || 0));
  const [roundOff, setRoundOff] = useState(sale.round_off_enabled !== false);
  const initialPayments = (sale.payments && sale.payments.length > 0)
    ? sale.payments.map((p) => ({ method: p.method, amount: p.amount, reference: p.reference || "" }))
    : [{ method: sale.payment_method || "cash", amount: sale.grand_total || 0, reference: "" }];
  const [payments, setPayments] = useState(initialPayments);
  const [saving, setSaving] = useState(false);
  const [prodQ, setProdQ] = useState("");
  const [prodResults, setProdResults] = useState([]);
  const [discountReason, setDiscountReason] = useState(sale.discount_reason || "");
  const [policy, setPolicy] = useState(null);
  const [approval, setApproval] = useState(null);
  const [showApproval, setShowApproval] = useState(false);
  const [approvalReason, setApprovalReason] = useState("");
  const canOverridePrice = hasPerm(user, "pos.price_override") || !!approval;
  useEffect(() => { api.get("/settings/discount-policy").then((r) => setPolicy(r.data)).catch(() => {}); }, []);

  // Search store inventory to ADD a new product to this bill
  useEffect(() => {
    const q = prodQ.trim();
    if (q.length < 2) { setProdResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/store/${sale.store_id}/inventory?q=${encodeURIComponent(q)}`)
        .then((r) => setProdResults((r.data || []).slice(0, 8)))
        .catch(() => setProdResults([]));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodQ]);

  const addProduct = (p) => {
    const idx = items.findIndex((it) => it.sku === p.sku);
    if (idx >= 0) {
      const c = [...items]; c[idx].qty = (parseInt(c[idx].qty) || 0) + 1; setItems(c);
    } else {
      setItems([...items, {
        sku: p.sku, product_name: p.product_name, barcode: p.barcode || "", hsn: p.hsn || "",
        gst_rate: p.gst_rate || 0, qty: 1, rate: p.selling_price || 0,
        discount: 0, discount_pct: 0, discount_mode: "flat",
      }]);
    }
    setProdQ(""); setProdResults([]);
  };

  const updateQty = (i, delta) => {
    const c = [...items]; c[i].qty = Math.max(1, (parseInt(c[i].qty) || 1) + delta); setItems(c);
  };
  const setRate = (i, v) => { const c = [...items]; c[i].rate = parseFloat(v) || 0; setItems(c); };
  const updateLine = (i, key, val) => {
    const c = [...items]; c[i] = { ...c[i], [key]: val }; setItems(c);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totals = useMemo(() => {
    // New POS sales (prices INCLUSIVE of GST) use the shared v2 engine; legacy
    // exclusive-price sales keep the old add-GST-on-top math.
    if (sale.price_includes_gst) return computePosTotals(items, discountMode, discountValue, roundOff);
    let sub = 0, tax = 0;
    const lineCalc = items.map((l) => {
      const gross = (parseInt(l.qty) || 0) * (parseFloat(l.rate) || 0);
      const pct = parseFloat(l.discount_pct) || 0;
      const flat = parseFloat(l.discount) || 0;
      const net = Math.max(0, gross - gross * Math.max(0, Math.min(100, pct)) / 100 - Math.max(0, flat));
      const r = (parseFloat(l.gst_rate) || 0) / 100;
      sub += net; tax += net * r;
      return { ...l, total: net * (1 + r) };
    });
    const subAfterTax = sub + tax;
    const dv = parseFloat(discountValue) || 0;
    const billDisc = discountMode === "percent" ? subAfterTax * Math.max(0, Math.min(100, dv)) / 100 : Math.min(Math.max(0, dv), subAfterTax);
    const afterDisc = Math.max(0, subAfterTax - billDisc);
    const rounded = roundOff ? Math.round(afterDisc) : afterDisc;
    return { gross: sub, lineDisc: 0, totalDiscount: billDisc, givenPct: 0, sub, tax, billDisc, rOff: roundOff ? rounded - afterDisc : 0, grand: rounded, lineCalc };
  }, [items, discountMode, discountValue, roundOff, sale.price_includes_gst]);
  const myCap = approval ? (policy?.limits?.[approval.approver.role] ?? 100) : (policy?.my_limit_pct ?? 100);
  const overCap = totals.givenPct > myCap + 1e-9;
  const needsReason = (policy?.require_reason ?? true) && totals.totalDiscount > 0;

  useEffect(() => {
    if (payments.length === 1) {
      setPayments([{ ...payments[0], amount: totals.grand }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals.grand]);

  const paymentSum = useMemo(() => payments.reduce((a, p) => a + (parseFloat(p.amount) || 0), 0), [payments]);
  const paymentDelta = +(totals.grand - paymentSum).toFixed(2);

  const updatePayment = (i, key, val) => {
    const c = [...payments]; c[i] = { ...c[i], [key]: val }; setPayments(c);
  };
  const addPayment = () => {
    const used = new Set(payments.map((p) => p.method));
    const next = PAYMENT_METHODS.find((m) => !used.has(m)) || "other";
    setPayments([...payments, { method: next, amount: Math.max(0, paymentDelta), reference: "" }]);
  };
  const removePayment = (i) => setPayments(payments.filter((_, idx) => idx !== i));
  const autoBalanceLast = () => {
    if (payments.length === 0) return;
    const others = payments.slice(0, -1).reduce((a, p) => a + (parseFloat(p.amount) || 0), 0);
    const last = +(totals.grand - others).toFixed(2);
    const c = [...payments]; c[c.length - 1] = { ...c[c.length - 1], amount: Math.max(0, last) }; setPayments(c);
  };

  const save = async () => {
    if (items.length === 0) return;
    if (Math.abs(paymentDelta) > 0.05) {
      alert(`Payment mismatch: ${inr(paymentDelta)} remaining. Adjust payment amounts.`);
      return;
    }
    if (overCap && !approval) { setApprovalReason(`Discount ${totals.givenPct}% exceeds your ${myCap}% limit.`); setShowApproval(true); return; }
    if (needsReason && !discountReason.trim()) { alert("Please enter a reason for the discount."); return; }
    setSaving(true);
    try {
      const body = {
        discount_reason: discountReason.trim(),
        approval_token: approval?.approval_token || "",
        items: items.map((it) => ({
          sku: it.sku, product_name: it.product_name, barcode: it.barcode || "",
          hsn: it.hsn || "", gst_rate: parseFloat(it.gst_rate) || 0,
          qty: parseInt(it.qty) || 0, rate: parseFloat(it.rate) || 0,
          discount: parseFloat(it.discount) || 0,
          discount_pct: parseFloat(it.discount_pct) || 0,
        })),
        customer_name: customerName,
        customer_phone: phone,
        customer_address: address,
        ship_to_different: shipToDifferent,
        ship_to_name: shipToDifferent ? shipToName : "",
        ship_to_phone: shipToDifferent ? shipToPhone : "",
        ship_to_address: shipToDifferent ? shipToAddress : "",
        notes,
        discount_total: discountMode === "flat" ? (parseFloat(discountValue) || 0) : 0,
        discount_pct: discountMode === "percent" ? (parseFloat(discountValue) || 0) : 0,
        round_off_enabled: roundOff,
        payments: payments.map((p) => ({
          method: p.method, amount: parseFloat(p.amount) || 0, reference: p.reference || "",
        })),
      };
      await api.patch(`/store/sales/${sale.id}`, body);
      onSaved();
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === "DISCOUNT_LIMIT" || code === "PRICE_LOCKED") { setApproval(null); setApprovalReason(apiErrorMessage(e)); setShowApproval(true); }
      else alert("Save failed: " + apiErrorMessage(e));
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="edit-invoice-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">Edit Invoice {sale.invoice_no}</div>
            <div className="text-xs text-amber-700">⚠ {isAdmin ? "Admin edit — no time limit. Changes are audited." : "Only editable within 1 hour of creation. Changes are audited."}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <input data-testid="edit-customer-name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer" className="h-10 px-3 border border-slate-300 rounded-md text-sm col-span-2" />
            <input data-testid="edit-customer-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
          </div>
          <textarea
            data-testid="edit-customer-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={2}
            placeholder="Customer address (printed on invoice)"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none"
          />
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input data-testid="edit-ship-to-toggle" type="checkbox" checked={shipToDifferent} onChange={(e) => setShipToDifferent(e.target.checked)} className="w-4 h-4" />
              <span className="text-xs font-semibold text-slate-700">Ship to a different address</span>
            </label>
            {shipToDifferent && (
              <div className="mt-2 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <input data-testid="edit-ship-to-name" value={shipToName} onChange={(e) => setShipToName(e.target.value)} placeholder="Recipient name" className="h-9 px-3 border border-slate-300 rounded-md text-sm col-span-2" />
                  <input data-testid="edit-ship-to-phone" value={shipToPhone} onChange={(e) => setShipToPhone(e.target.value)} placeholder="Phone" className="h-9 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
                </div>
                <textarea
                  data-testid="edit-ship-to-address"
                  value={shipToAddress}
                  onChange={(e) => setShipToAddress(e.target.value)}
                  rows={2}
                  placeholder="Delivery address"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none"
                />
              </div>
            )}
          </div>
          {/* Add product to bill */}
          <div className="relative">
            <input
              data-testid="edit-add-product-search"
              value={prodQ}
              onChange={(e) => setProdQ(e.target.value)}
              placeholder="+ Add product — search by name / SKU / barcode…"
              className="w-full h-10 px-3 border border-emerald-300 bg-emerald-50/40 rounded-md text-sm"
            />
            {prodResults.length > 0 && (
              <div className="absolute left-0 right-0 top-11 bg-white border border-slate-200 rounded-md shadow-lg z-20 max-h-56 overflow-y-auto" data-testid="edit-add-product-results">
                {prodResults.map((p) => (
                  <button key={p.sku} onClick={() => addProduct(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                    <div className="text-sm font-medium">{p.product_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono-num">{p.sku} · Stk {p.qty} · {inr(p.selling_price || 0)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Product</th>
                  <th className="text-right px-3 py-2 font-semibold">GST%</th>
                  <th className="text-right px-3 py-2 font-semibold">Qty</th>
                  <th className="text-right px-3 py-2 font-semibold">Rate</th>
                  <th className="text-right px-3 py-2 font-semibold">Disc</th>
                  <th className="text-right px-3 py-2 font-semibold">Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((l, i) => (
                  <tr key={l.sku + i} className="border-b border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{l.product_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono-num">{l.sku}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num">{(l.gst_rate || 0).toFixed(1)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center border border-slate-300 rounded-md overflow-hidden">
                        <button onClick={() => updateQty(i, -1)} className="w-6 h-7 hover:bg-slate-100 flex items-center justify-center"><Minus size={10} /></button>
                        <span className="w-8 text-center text-xs font-mono-num font-semibold">{l.qty}</span>
                        <button onClick={() => updateQty(i, 1)} className="w-6 h-7 hover:bg-slate-100 flex items-center justify-center"><Plus size={10} /></button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="relative inline-block" title={canOverridePrice ? "Selling price" : "Selling price is locked — use Discount or get manager approval"}>
                        <input type="number" step="0.01" value={l.rate} readOnly={!canOverridePrice} onChange={(e) => setRate(i, e.target.value)} data-testid={`edit-rate-${i}`} className={`w-24 h-7 px-2 border rounded-md text-xs font-mono-num text-right ${canOverridePrice ? "border-slate-300" : "border-slate-200 bg-slate-100 text-slate-600 pr-6 cursor-not-allowed"}`} />
                        {!canOverridePrice && <Lock size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400" />}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <div className="inline-flex border border-slate-300 rounded-md overflow-hidden text-[10px]">
                          <button onClick={() => updateLine(i, "discount_mode", "flat")} className={`px-1.5 py-0.5 ${(l.discount_mode || "flat") === "flat" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>₹</button>
                          <button onClick={() => updateLine(i, "discount_mode", "percent")} className={`px-1.5 py-0.5 ${l.discount_mode === "percent" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>%</button>
                        </div>
                        {(l.discount_mode || "flat") === "flat" ? (
                          <input type="number" step="0.01" value={l.discount || 0} onChange={(e) => updateLine(i, "discount", parseFloat(e.target.value) || 0)} className="w-14 h-6 px-1 border border-slate-300 rounded-md text-[10px] font-mono-num text-right" />
                        ) : (
                          <input type="number" step="0.1" value={l.discount_pct || 0} onChange={(e) => updateLine(i, "discount_pct", parseFloat(e.target.value) || 0)} className="w-14 h-6 px-1 border border-slate-300 rounded-md text-[10px] font-mono-num text-right" />
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr(totals.lineCalc[i]?.total || 0)}</td>
                    <td className="px-3 py-2 text-right"><button onClick={() => remove(i)} className="text-red-600 hover:bg-red-50 p-1 rounded-md"><X size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-3 gap-3 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Bill Discount</label>
              <div className="flex items-center gap-1">
                <div className="inline-flex border border-slate-300 rounded-md overflow-hidden">
                  <button onClick={() => setDiscountMode("flat")} className={`px-2 h-9 text-xs ${discountMode === "flat" ? "bg-slate-900 text-white" : "bg-white"}`}>₹</button>
                  <button onClick={() => setDiscountMode("percent")} className={`px-2 h-9 text-xs ${discountMode === "percent" ? "bg-slate-900 text-white" : "bg-white"}`}>%</button>
                </div>
                <input data-testid="edit-bill-discount" type="number" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="flex-1 h-9 px-2 border border-slate-300 rounded-md text-sm font-mono-num text-right" />
              </div>
              <div className="text-[10px] text-slate-500 mt-1 font-mono-num">- {inr(totals.billDisc)}</div>
            </div>
            {policy && sale.price_includes_gst && (
              <div className={`col-span-3 rounded-md border px-3 py-2 text-xs ${overCap ? "bg-red-50 border-red-200" : totals.totalDiscount > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`} data-testid="edit-discount-meter">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-600 inline-flex items-center gap-1"><ShieldCheck size={11} /> Discount {totals.givenPct}% · limit {myCap}% · taxable {inr(totals.sub)} · GST {inr(totals.tax)}</span>
                  {approval
                    ? <span className="text-[10px] font-bold text-emerald-700">Approved by {approval.approver.name || approval.approver.email}</span>
                    : overCap && <button onClick={() => { setApprovalReason(`Discount ${totals.givenPct}% exceeds your ${myCap}% limit.`); setShowApproval(true); }} data-testid="edit-get-approval-btn" className="text-[10px] font-bold text-red-700 hover:underline">Get manager approval →</button>}
                </div>
                {needsReason && <input value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} placeholder="Reason for discount (required)" data-testid="edit-discount-reason" className={`mt-1.5 w-full h-8 px-2 border rounded-md text-xs ${discountReason.trim() ? "border-slate-300" : "border-amber-400"}`} />}
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Round-off</label>
              <label className="inline-flex items-center gap-2 h-9 cursor-pointer">
                <input data-testid="edit-round-off" type="checkbox" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)} className="w-4 h-4" />
                <span className="text-sm">Nearest ₹</span>
              </label>
              <div className={`text-[10px] font-mono-num mt-1 ${totals.rOff < 0 ? "text-red-600" : totals.rOff > 0 ? "text-emerald-600" : "text-slate-400"}`}>
                {totals.rOff !== 0 ? (totals.rOff > 0 ? "+" : "") + totals.rOff.toFixed(2) : "—"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Grand Total</div>
              <div className="font-display text-2xl font-bold font-mono-num" data-testid="edit-grand-total">{inr(totals.grand)}</div>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Payments</div>
              {payments.length < 3 && <button onClick={addPayment} data-testid="edit-add-payment" className="text-[10px] font-bold text-emerald-700 hover:underline inline-flex items-center gap-0.5"><Plus size={10} weight="bold" /> Split</button>}
            </div>
            {payments.map((p, i) => (
              <div key={i} className="flex items-center gap-2 mb-1.5" data-testid={`edit-payment-row-${i}`}>
                <select value={p.method} onChange={(e) => updatePayment(i, "method", e.target.value)} className="h-8 px-2 border border-slate-300 rounded-md text-xs uppercase font-semibold">
                  {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <input type="number" step="0.01" value={p.amount} onChange={(e) => updatePayment(i, "amount", e.target.value)} className="flex-1 h-8 px-2 border border-slate-300 rounded-md text-xs font-mono-num text-right" />
                {(p.method === "card" || p.method === "upi") && (
                  <input type="text" value={p.reference} onChange={(e) => updatePayment(i, "reference", e.target.value)} placeholder="Ref" className="w-20 h-8 px-2 border border-slate-300 rounded-md text-xs font-mono-num" />
                )}
                {payments.length > 1 && <button onClick={() => removePayment(i)} className="p-1 text-red-600 hover:bg-red-50 rounded-md"><X size={12} /></button>}
              </div>
            ))}
            {payments.length > 1 && (
              <button onClick={autoBalanceLast} className="text-[10px] font-semibold text-slate-600 hover:text-slate-900 mt-1">↩ Auto-balance last to {inr(totals.grand)}</button>
            )}
            {Math.abs(paymentDelta) > 0.05 && (
              <div className={`text-xs font-semibold mt-1 ${paymentDelta > 0 ? "text-red-600" : "text-amber-600"}`} data-testid="edit-payment-mismatch">
                {paymentDelta > 0 ? `Short by ${inr(paymentDelta)}` : `Over by ${inr(-paymentDelta)}`}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Notes / Remarks</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Printed on the invoice PDF"
              data-testid="edit-notes"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none"
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={save} disabled={saving || items.length === 0 || Math.abs(paymentDelta) > 0.05} data-testid="save-edit-invoice" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md disabled:opacity-50">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
      {showApproval && <ApprovalModal reason={approvalReason} onClose={() => setShowApproval(false)} onApproved={(d) => { setApproval(d); setShowApproval(false); }} />}
    </div>
  );
}

function HistoryModal({ data, onClose }) {
  const { sale, entries } = data;
  const actionColor = (a) => ({
    create: "bg-emerald-50 text-emerald-700",
    update: "bg-blue-50 text-blue-700",
    cancel: "bg-red-50 text-red-700",
  }[a] || "bg-slate-100 text-slate-700");
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="history-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">History — {sale.invoice_no}</div>
            <div className="text-xs text-slate-500">Audit trail for this invoice.</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {entries.length === 0 && <div className="text-sm text-slate-400 text-center py-8">No history entries.</div>}
          {entries.map((a) => (
            <div key={a.id} className="border border-slate-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${actionColor(a.action)}`}>{a.action}</span>
                  <span className="text-xs text-slate-500 font-mono-num">{fmtIST(a.created_at)}</span>
                </div>
                <div className="text-xs text-slate-600">{a.user_email}</div>
              </div>
              {a.action === "update" && a.before && a.after && (
                <div className="text-[11px] text-slate-600 mt-1 font-mono-num">
                  Total: <span className="text-red-600">{a.before.grand_total}</span> → <span className="text-emerald-600">{a.after.grand_total}</span>
                  {" · "}Items: {a.before.items?.length} → {a.after.items?.length}
                </div>
              )}
              {a.action === "create" && a.after && (
                <div className="text-[11px] text-slate-600 mt-1 font-mono-num">
                  Grand total: {a.after.grand_total} · {a.after.items?.length} items
                </div>
              )}
              {a.action === "cancel" && (
                <div className="text-[11px] text-red-600 mt-1">Stock restored. Invoice voided.</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function ShareDialog({ data, onClose }) {
  const { invoice, wa_url, download_url, text, phone, expires_at } = data;
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedText, setCopiedText] = useState(false);

  const copy = async (val, kind) => {
    try {
      await navigator.clipboard.writeText(val);
      if (kind === "url") { setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 1800); }
      else { setCopiedText(true); setTimeout(() => setCopiedText(false), 1800); }
    } catch { /* clipboard blocked — fallback */ }
  };

  const expiry = expires_at ? new Date(expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "30 days";

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="share-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg inline-flex items-center gap-2">
              <WhatsappLogo size={18} weight="fill" className="text-emerald-600" /> Share Invoice {invoice.invoice_no}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Customer can download the invoice from the link below — no login required. Link valid until <span className="font-mono-num font-semibold">{expiry}</span>.
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">Public download URL</label>
            <div className="flex gap-2">
              <input
                readOnly
                value={download_url}
                data-testid="share-download-url"
                className="flex-1 h-10 px-3 border border-slate-300 rounded-md text-xs font-mono-num bg-slate-50 truncate"
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={() => copy(download_url, "url")}
                data-testid="copy-share-url"
                className="h-10 px-3 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-md inline-flex items-center gap-1.5 shrink-0"
              >
                {copiedUrl ? <><CheckCircle size={13} weight="fill" /> Copied</> : <><Copy size={13} weight="bold" /> Copy</>}
              </button>
              <a
                href={download_url}
                target="_blank"
                rel="noreferrer"
                data-testid="preview-share-url"
                className="h-10 px-3 border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-md inline-flex items-center gap-1.5 shrink-0"
              >
                <Download size={13} weight="bold" /> Open
              </a>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">WhatsApp message</label>
            <textarea
              readOnly
              value={text}
              rows={5}
              data-testid="share-wa-text"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-xs bg-slate-50 resize-none font-mono-num leading-relaxed"
            />
            <button
              onClick={() => copy(text, "text")}
              className="mt-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              {copiedText ? <><CheckCircle size={11} weight="fill" /> Copied to clipboard</> : <><Copy size={11} weight="bold" /> Copy message</>}
            </button>
          </div>

          {!phone && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-[11px] text-amber-800">
              No phone number on this invoice — WhatsApp will open and ask you to pick a contact.
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Close</button>
          <a
            href={wa_url}
            target="_blank"
            rel="noreferrer"
            data-testid="open-whatsapp"
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md inline-flex items-center gap-1.5"
          >
            <WhatsappLogo size={15} weight="fill" /> Open WhatsApp
            <ArrowSquareOut size={11} weight="bold" />
          </a>
        </div>
      </div>
    </div>
  );
}
